import React, { useState, useEffect } from "react";
import { FileDown, ArrowLeft, BarChart3 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import ScoutingReportManager from "../managers/ScoutingReportsManager";
import { SprayChart } from "../components/scouting/SprayChart";
import { fetchAPI, fetchAPIBlob } from "../config/api";
import { saveAs } from "file-saver";

export const SprayChartsPage = () => {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sprayChartData, setSprayChartData] = useState({});
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    const fetchReportAndData = async () => {
      try {
        setLoading(true);
        const reportData = await ScoutingReportManager.getReportById(reportId);
        setReport(reportData);

        if (reportData) {
          const batters = reportData.positionPlayers || [];
          const playerIds = batters.map(b => b.id).filter(Boolean);
          
          if (playerIds.length > 0) {
            const bulkData = await fetchAPI('/spraychart_data/bulk', {
              method: 'POST',
              body: JSON.stringify({
                player_ids: playerIds,
                year: reportData.year || 2025,
                division: reportData.division || 3,
              }),
            });
            setSprayChartData(bulkData || {});
          }
        }
      } catch (error) {
        console.error("Error fetching report:", error);
      } finally {
        setLoading(false);
      }
    };

    if (reportId) {
      fetchReportAndData();
    }
  }, [reportId]);

  const handleExportSingle = async (player) => {
    if (exporting) return;
    setExporting(true);
    
    try {
      const params = new URLSearchParams({
        player_id: player.id,
        year: report?.year || 2025,
        division: report?.division || 3,
        hand_filter: "all",
      });
      
      const blob = await fetchAPIBlob(`/reports/spray_chart?${params.toString()}`);
      const filename = `${(player.name || 'spraychart').replace(/\s+/g, '_')}_${report?.year || 2025}.png`;
      saveAs(blob, filename);
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setExporting(false);
    }
  };

  const handleExportAll = async () => {
    if (exporting) return;
    const batters = report?.positionPlayers || [];
    if (batters.length === 0) return;

    setExporting(true);
    
    try {
      const blob = await fetchAPIBlob('/reports/spray_charts_bulk', {
        method: 'POST',
        body: JSON.stringify({
          players: batters.map(b => ({ id: b.id, name: b.name })),
          year: report?.year || 2025,
          division: report?.division || 3,
          team_name: report?.teamName || 'team',
        }),
      });
      
      const teamName = (report?.teamName || "team").replace(/[^a-z0-9]/gi, "_").toLowerCase();
      saveAs(blob, `${teamName}_spray_charts_${report?.year || 2025}.pdf`);
    } catch (error) {
      console.error("Error exporting spray charts:", error);
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <div className="container max-w-6xl mx-auto px-8 py-16">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 min-h-[400px] flex justify-center items-center">
            <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
        <div className="container max-w-6xl mx-auto px-8 py-16">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 min-h-[400px] flex flex-col justify-center items-center text-center">
            <h2 className="text-xl font-bold text-gray-800 mb-2">Report Not Found</h2>
            <p className="text-gray-600 mb-4">The requested report could not be found.</p>
            <button
              onClick={() => navigate("/scouting")}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Back to Reports
            </button>
          </div>
        </div>
      </div>
    );
  }

  const batters = report.positionPlayers || [];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100">
      <div className="container max-w-6xl mx-auto px-8 py-16">
        <div className="relative z-10 mb-6">
          <div className="relative overflow-hidden rounded-2xl border border-white/30 bg-white/60 backdrop-blur p-5 shadow-xl">
            <div className="absolute -top-10 -right-10 w-36 h-36 rounded-full bg-gradient-to-br from-blue-400/20 to-indigo-400/20 blur-2xl" />
            <div className="relative z-10 flex items-start gap-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white font-bold flex-shrink-0">
                i
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-base font-semibold text-gray-800 mb-1">Spray Charts</div>
                <div className="text-sm text-gray-600">
                  View and export spray charts for all batters in this scouting report.
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
          <div className="flex flex-wrap gap-3 mb-6">
            <button
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft size={14} />
              Back
            </button>
            <button
              onClick={handleExportAll}
              disabled={exporting || batters.length === 0}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                exporting || batters.length === 0
                  ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-600 to-indigo-600 text-white hover:from-blue-700 hover:to-indigo-700"
              }`}
            >
              <FileDown size={14} />
              {exporting ? "Exporting..." : "Export All"}
            </button>
          </div>

          {batters.length === 0 ? (
            <div className="text-center py-10">
              <div className="bg-blue-50 p-6 rounded-xl inline-flex flex-col items-center">
                <div className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center mb-4">
                  <BarChart3 size={24} />
                </div>
                <p className="text-gray-800 text-lg font-medium">No batters in this report</p>
                <p className="text-gray-500 mt-2">Add batters to generate spray charts</p>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {batters.map((batter) => (
                <div
                  key={batter.id}
                  className="border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow bg-white"
                >
                  <SprayChart
                    playerId={batter.id}
                    year={report.year || 2025}
                    division={report.division || 3}
                    prefetchedData={sprayChartData[batter.id]}
                  />

                  <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm">
                      <div className="text-center">
                        <div className="font-semibold text-gray-800">{batter.keyStats?.avg || ".---"}</div>
                        <div className="text-xs text-gray-500">AVG</div>
                      </div>
                      <div className="text-center">
                        <div className="font-semibold text-gray-800">{batter.keyStats?.obp || ".---"}</div>
                        <div className="text-xs text-gray-500">OBP</div>
                      </div>
                      <div className="text-center">
                        <div className="font-semibold text-gray-800">{batter.keyStats?.slg || ".---"}</div>
                        <div className="text-xs text-gray-500">SLG</div>
                      </div>
                    </div>

                    <button
                      onClick={() => handleExportSingle(batter)}
                      disabled={exporting}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg transition-colors ${
                        exporting
                          ? "bg-gray-200 text-gray-400 cursor-not-allowed"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                    >
                      <FileDown size={14} />
                      Export
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
